import { DicomImage, NativePixelDecoder } from 'dcmjs-imaging';

const DICOM_MAGIC = 'DICM';
const DICOM_MAGIC_OFFSET = 128;
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

export interface IDicomConversionResult {
	file: File;
	width: number;
	height: number;
	sourceName: string;
}

let decoderInitPromise: Promise<void> | null = null;

const ensureNativeDecoderInitialized = async (): Promise<void> => {
	if (NativePixelDecoder.isInitialized()) return;

	if (!decoderInitPromise) {
		decoderInitPromise = NativePixelDecoder.initializeAsync({
			webAssemblyModulePathOrUrl: `${process.env.PUBLIC_URL || ''}/dcmjs-native-codecs.wasm`,
		}).catch((error) => {
			decoderInitPromise = null;
			throw error;
		});
	}

	await decoderInitPromise;
};

const hasDicomExtension = (fileName: string): boolean => {
	const lower = fileName.toLowerCase();
	return lower.endsWith('.dcm') || lower.endsWith('.dicom');
};

const readFileSlice = async (
	file: File,
	start: number,
	end: number,
): Promise<ArrayBuffer> => {
	return file.slice(start, end).arrayBuffer();
};

const hasDicomMagic = async (file: File): Promise<boolean> => {
	if (file.size < DICOM_MAGIC_OFFSET + DICOM_MAGIC.length) {
		return false;
	}

	const buffer = await readFileSlice(
		file,
		DICOM_MAGIC_OFFSET,
		DICOM_MAGIC_OFFSET + DICOM_MAGIC.length,
	);
	const magic = new TextDecoder('ascii').decode(buffer);
	return magic === DICOM_MAGIC;
};

/**
 * Content-first DICOM detection. Extension/MIME are optional shortcuts;
 * extensionless files are accepted when the DICOM magic is present.
 */
export const isDicomFile = async (file: File): Promise<boolean> => {
	if (file.type === 'application/dicom' || hasDicomExtension(file.name)) {
		return true;
	}

	return hasDicomMagic(file);
};

export const isImageMimeType = (file: File): boolean => {
	return IMAGE_MIME_TYPES.includes(file.type);
};

const buildPngFileName = (sourceName: string): string => {
	const trimmed = sourceName.trim();
	if (!trimmed) {
		return 'dicom-image.png';
	}

	const lower = trimmed.toLowerCase();
	if (lower.endsWith('.dcm') || lower.endsWith('.dicom')) {
		return `${trimmed.replace(/\.(dcm|dicom)$/i, '')}.png`;
	}

	if (/\.[a-z0-9]+$/i.test(trimmed)) {
		return `${trimmed.replace(/\.[^.]+$/, '')}.png`;
	}

	return `${trimmed}.png`;
};

const rgbaBufferToPngBlob = (
	pixels: ArrayBuffer,
	width: number,
	height: number,
): Promise<Blob> => {
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;

	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return Promise.reject(new Error('Unable to create canvas context'));
	}

	const expectedLength = width * height * 4;
	const source = new Uint8ClampedArray(pixels);
	if (source.length < expectedLength) {
		return Promise.reject(
			new Error('DICOM pixel data does not match image dimensions'),
		);
	}
	const rgba = source.slice(0, expectedLength);
	const imageData = new ImageData(rgba, width, height);
	ctx.putImageData(imageData, 0, 0);

	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (!blob) {
				reject(new Error('Failed to encode DICOM frame as PNG'));
				return;
			}
			resolve(blob);
		}, 'image/png');
	});
};

export const convertDicomToPngFile = async (
	file: File,
): Promise<IDicomConversionResult> => {
	await ensureNativeDecoderInitialized();

	const arrayBuffer = await file.arrayBuffer();
	const image = new DicomImage(arrayBuffer);
	const renderingResult = image.render({
		frame: 0,
		renderOverlays: true,
	});

	const { width, height, pixels } = renderingResult;
	if (!width || !height || !pixels) {
		throw new Error('DICOM file does not contain a renderable image frame');
	}

	const pngBlob = await rgbaBufferToPngBlob(pixels, width, height);
	const sourceName = file.name || 'dicom-image';
	const pngFile = new File([pngBlob], buildPngFileName(sourceName), {
		type: 'image/png',
		lastModified: Date.now(),
	});

	return {
		file: pngFile,
		width,
		height,
		sourceName,
	};
};
